import React from "react";
import { MathJax } from "better-react-mathjax";

export function DecompositionIntro({ lite = false }) {
  const baseClass = lite
    ? 'space-y-2 text-justify hyphens-auto text-sm leading-relaxed text-white/80 md:text-base'
    : 'space-y-4 text-justify hyphens-auto leading-relaxed text-white/85 text-sm md:text-base';
  return (
    <div className={baseClass}>
      Matrix factorizations are the backbone of numerical linear algebra. By decomposing a matrix into
      structured components, they transform fundamental tasks—solving linear systems, least-squares fitting,
      and eigenvalue computations—into sequences of stable, highly optimized kernels. In practice, three pillars
      dominate dense computations: <em>LU</em> (general direct solves with pivoting), <em>Cholesky</em> (fast
      triangular solves specialized to symmetric positive definite matrices), and <em>QR</em> (orthogonalization
      that underlies robust least-squares and forms the gateway to eigenvalue algorithms via the QR iteration).
      These factorizations are not merely algebraic identities; they encode numerical stability properties,
      reveal rank and conditioning, and map cleanly onto modern BLAS/LAPACK implementations for high performance.
    </div>
  );
}

export default function MatrixDecompositionsLongExplanation() {
  return (
    <div className="space-y-6 text-sm leading-relaxed text-white/80 text-justify hyphens-auto md:text-base">
      <h2 className="text-sm font-semibold md:text-base">1. Motivation & Use Cases</h2>
      <ul className="list-disc pl-6 space-y-1">
        <li>
          <strong>Direct linear solves:</strong> Factor once (e.g., <em>LU</em> or <em>Cholesky</em>) and reuse the
          factors to solve many right-hand sides via inexpensive triangular substitutions. 
          This amortizes the <MathJax inline>{"\\( \\mathcal{O}(n^3) \\)"}</MathJax> setup cost across multiple solves.
        </li>
        <li>
          <strong>Least squares:</strong> <em>QR</em> yields numerically stable solutions to overdetermined systems
          without forming normal equations. Pivoted QR also detects and manages rank deficiency.
        </li>
        <li>
          <strong>Eigenvalues:</strong> The <em>QR iteration</em> (especially with shifts) is a core dense-matrix
          algorithm; it operates on a Hessenberg (or tridiagonal) form to achieve
          <MathJax inline>{"\\( \\mathcal{O}(n^2) \\)"}</MathJax> work per step.
        </li>
        <li>
          <strong>Performance:</strong> Blocked, BLAS-3 implementations exploit cache locality and vectorization,
          delivering high throughput on modern hardware. Orthogonal transformations (Householder/Givens) are
          preferred for stability in finite precision.
        </li>
      </ul>

      <h2 className="text-sm font-semibold mt-4 md:text-base">2. Notation & Goals</h2>
      <p>
        For <MathJax inline>{"\\( A\\in\\mathbb{R}^{m\\times n} \\)"}</MathJax>, the following canonical targets are used:
        <em>LU</em> seeks <MathJax inline>{"\\( PA=LU \\)"}</MathJax> with <MathJax inline>{"\\( L \\)"}</MathJax> unit lower triangular
        and <MathJax inline>{"\\( U \\)"}</MathJax> upper triangular; row permutations in <MathJax inline>{"\\( P \\)"}</MathJax> provide
        numerical stability. <em>Cholesky</em> targets <MathJax inline>{"\\( A=LL^\\top \\)"}</MathJax> for symmetric positive
        definite (SPD) matrices, producing a unique lower-triangular factor <MathJax inline>{"\\( L \\)"}</MathJax> with positive 
        diagonal. <em>QR</em> seeks <MathJax inline>{"\\( A=QR \\)"}</MathJax> with <MathJax inline>{"\\( Q \\)"}</MathJax> orthogonal (<MathJax inline>{"\\( Q^\\top Q=I \\)"}</MathJax>) and <MathJax inline>{"\\( R \\)"}</MathJax> upper triangular. Each factorization
        exposes a structure that is easy to invert or apply, turning general problems into sequences of triangular
        solves and orthogonal transformations with predictable stability.
      </p>

      <h2 className="text-sm font-semibold mt-4 md:text-base">3. LU Factorization</h2>
      <h3 className="font-semibold mt-2">Existence & Pivoting</h3>
      <ul className="list-disc pl-6 space-y-1">
        <li>
          Without pivoting, an LU factorization of a square matrix exists if all leading principal minors are
          nonzero. In realistic computations, we employ <em>partial pivoting</em> to avoid zero or tiny pivots and
          reduce element growth: <MathJax inline>{"\\( PA=LU \\)"}</MathJax> with a 
          permutation matrix <MathJax inline>{"\\( P \\)"}</MathJax> capturing row swaps chosen by maximum magnitude in the current column.
        </li>
        <li>
          <em>Uniqueness:</em> If <MathJax inline>{"\\( L \\)"}</MathJax> is constrained to be unit lower triangular,
          then <MathJax inline>{"\\( P,L,U \\)"}</MathJax> are uniquely determined. In block or pivoted variants,
          uniqueness is defined up to the specific pivoting strategy.
        </li>
      </ul>

      <h3 className="font-semibold mt-2">Algorithm (Doolittle form)</h3>
      <ol className="list-decimal pl-6 space-y-1">
        <li>
          For <MathJax inline>{"\\( k=1,\\ldots,n-1 \\)"}</MathJax>: choose pivot row <MathJax inline>{"\\( p\\ge k \\)"}</MathJax> maximizing <MathJax inline>{"\\( |a_{pk}| \\)"}</MathJax>, swap rows
          (recorded in <MathJax inline>{"\\( P \\)"}</MathJax>).
        </li>
        <li>
          Form multipliers <MathJax inline>{"\\( \\ell_{ik}=a_{ik}/a_{kk} \\)"}</MathJax> for <MathJax inline>{"\\( i=k+1,\\ldots,n \\)"}</MathJax>.
        </li>
        <li>
          Update the trailing submatrix: <MathJax inline>{"\\( A_{i,k+1:n}\\leftarrow A_{i,k+1:n}-\\ell_{ik}A_{k,k+1:n} \\)"}</MathJax>. This is a rank-1 update
          that zeros the subdiagonal in column <MathJax inline>{"\\( k \\)"}</MathJax>.
        </li>
      </ol>
      <p>
        <strong>Complexity:</strong> about <MathJax inline>{"\\( \\tfrac{2}{3}n^3 \\)"}</MathJax> flops to factor a dense
        <MathJax inline>{"\\( n\\times n \\)"}</MathJax> matrix; each triangular solve is <MathJax inline>{"\\( \\mathcal{O}(n^2) \\)"}</MathJax>. <strong>Stability:</strong> Partial pivoting is generally
        backward stable; although pathological growth examples exist, they are uncommon in practice. Scaling of rows/columns
        can further improve robustness when entries vary greatly in magnitude.
      </p>

      <h2 className="text-sm font-semibold mt-4 md:text-base">4. LU Example (no pivoting)</h2>
      <MathJax>{String.raw`\[
      \begin{aligned}
        A&=\begin{bmatrix}
          2 & 1 & 1\\
          4 & -6 & 0\\
          -2 & 7 & 2
        \end{bmatrix}\\
        &=
        \underbrace{\begin{bmatrix}
          1 & 0 & 0\\
          2 & 1 & 0\\
          -1 & -1 & 1
        \end{bmatrix}}_{L}
        \underbrace{\begin{bmatrix}
          2 & 1 & 1\\
          0 & -8 & -2\\
          0 & 0 & 1
        \end{bmatrix}}_{U}.
        \end{aligned}
      \]`}</MathJax>
      <p>
        Verification confirms <MathJax inline>{"\\( L \\)"}</MathJax> is unit lower triangular,
        <MathJax inline>{"\\( U \\)"}</MathJax> is upper triangular, and <MathJax inline>{"\\( A=LU \\)"}</MathJax>.
        In a practical solver one would add partial pivoting to guard against small pivots and improve numerical stability.
      </p>

      <h2 className="text-sm font-semibold mt-4 md:text-base">5. Cholesky Factorization (SPD Matrices)</h2>
      <p>
        If <MathJax inline>{"\\( A=A^\\top\\succ0 \\)"}</MathJax>, there exists a unique Cholesky factorization
        <MathJax inline>{"\\( A=LL^\\top \\)"}</MathJax> with <MathJax inline>{"\\( L \\)"}</MathJax> lower triangular and positive diagonal.
        Cholesky is preferred whenever symmetry and positive definiteness hold: it halves the arithmetic (relative to LU),
        preserves symmetry, and is backward stable in floating-point arithmetic. Failure (negative or tiny pivot on the diagonal)
        signals that <MathJax inline>{"\\( A \\)"}</MathJax> is not SPD or that roundoff has contaminated the model.
      </p>
      <h3 className="font-semibold mt-2">Algorithm (left-looking)</h3>
      <MathJax>{String.raw`\[
      \begin{aligned}
        L_{ii}&=\sqrt{A_{ii}-\sum_{k=1}^{i-1}L_{ik}^2},\\
        L_{ji}&=\frac{A_{ji}-\sum_{k=1}^{i-1}L_{jk}L_{ik}}{L_{ii}}\quad (j=i+1,\dots,n).
        \end{aligned}
      \]`}</MathJax>
      <p>
        <strong>Complexity:</strong> about <MathJax inline>{"\\( \\tfrac{1}{3}n^3 \\)"}</MathJax> flops (roughly half of LU)
        with excellent constant factors in blocked implementations. The resulting triangular solves are fast and exploit
        level-3 BLAS for high performance.
      </p>

      <h2 className="text-sm font-semibold mt-4 md:text-base">6. Cholesky Example</h2>
      <MathJax>{String.raw`\[
        A=\begin{bmatrix}
          4 & 2 & 2\\
          2 & 2 & 1\\
          2 & 1 & 3
        \end{bmatrix}
        =
        \underbrace{\begin{bmatrix}
          2 & 0 & 0\\
          1 & 1 & 0\\
          1 & 0 & \sqrt{2}
        \end{bmatrix}}_{L}
        \underbrace{\begin{bmatrix}
          2 & 1 & 1\\
          0 & 1 & 0\\
          0 & 0 & \sqrt{2}
        \end{bmatrix}}_{L^{\\top}}.
      \]`}</MathJax>
      <p>
        Here <MathJax inline>{"\\( A \\)"}</MathJax> is symmetric positive definite, so the Cholesky factor exists and is unique.
        The factorization reduces solves <MathJax inline>{"\\( Ax=b \\)"}</MathJax> to two triangular stages:
        first <MathJax inline>{"\\( Lz=b \\)"}</MathJax>, then <MathJax inline>{"\\( L^{\\top}x=z \\)"}</MathJax>.
      </p>

      <h2 className="text-sm font-semibold mt-4 md:text-base">7. QR Factorization</h2>
      <p>
        For <MathJax inline>{"\\( A\\in\\mathbb{R}^{m\\times n} \\)"}</MathJax> with <MathJax inline>{"\\( m\\ge n \\)"}</MathJax>, the 
        QR factorization <MathJax inline>{"\\( A=QR \\)"}</MathJax> represents the columns of <MathJax inline>{"\\( A \\)"}</MathJax> in an 
        orthonormal basis´(<MathJax inline>{"\\( Q \\)"}</MathJax>) with a triangular coefficient matrix <MathJax inline>{"\\( R \\)"}</MathJax>.
        In the economy-size form, <MathJax inline>{"\\( Q\\in\\mathbb{R}^{m\\times n} \\)"}</MathJax> and <MathJax inline>{"\\( R\\in\\mathbb{R}^{n\\times n} \\)"}</MathJax>. Orthogonality preserves 
        2-norms and condition numbers,
        making QR the method of choice for least-squares and as a component in eigenvalue algorithms.
      </p>

      <h3 className="font-semibold mt-2">Constructions</h3>
      <ul className="list-disc pl-6 space-y-1">
        <li>
          <strong>Classical Gram–Schmidt (CGS):</strong> algebraically simple but sensitive to roundoff when columns
          of <MathJax inline>{"\\( A \\)"}</MathJax> are nearly dependent; loss of orthogonality can be significant.
        </li>
        <li>
          <strong>Modified Gram–Schmidt (MGS):</strong> improves numerical behavior by reorthogonalizing progressively;
          still less robust than Householder for very ill-conditioned inputs.
        </li>
        <li>
          <strong>Householder reflectors:</strong> the standard, backward-stable approach. For a vector
          <MathJax inline>{"\\( x \\)"}</MathJax>, form <MathJax inline>{"\\( v=x\\pm\\|x\\|e_1 \\)"}</MathJax> and
          <MathJax inline>{"\\( H=I-2\\tfrac{vv^\\top}{v^\\top v} \\)"}</MathJax> to zero subdiagonal entries in one sweep.
          Blocked Householder variants attain high performance on modern hardware.
        </li>
        <li>
          <strong>Givens rotations:</strong> orthogonal plane rotations that zero one entry at a time; particularly
          effective for sparse, streaming, or structured problems where locality matters.
        </li>
      </ul>
      <p>
        <strong>Complexity:</strong> Householder QR costs <MathJax inline>{"\\( 2mn^2-\\tfrac{2}{3}n^3 \\)"}</MathJax> flops (
          square case <MathJax inline>{"\\( \\tfrac{2}{3}n^3 \\)"}</MathJax>). The orthogonal factor can be applied to vectors without
        forming it explicitly by storing compact Householder data.
      </p>

      <h2 className="text-sm font-semibold mt-4 md:text-base">8. QR Example (3×2 via Householder)</h2>
      <p>
        For <MathJax inline>{"\\( A=\\begin{bmatrix}1&1\\\\1&0\\\\0&1\\end{bmatrix} \\)"}</MathJax>, one valid factorization is
      </p>
      <MathJax>{String.raw`\[
      \begin{aligned}
        Q&=\begin{bmatrix}
          \tfrac{\sqrt{2}}{2} & \tfrac{\sqrt{6}}{6} & 0\\
          \tfrac{\sqrt{2}}{2} & -\tfrac{\sqrt{6}}{6} & 0\\
          0 & \tfrac{\sqrt{6}}{3} & 1
        \end{bmatrix},\quad
        R=\begin{bmatrix}
          \sqrt{2} & \tfrac{\sqrt{2}}{2}\\
          0 & \tfrac{\sqrt{6}}{2}\\
          0 & 0
        \end{bmatrix},\\
        A&=Q_{(:,1:2)}R_{(1:2,:)}.
        \end{aligned}
      \]`}</MathJax>
      <p>
        The factorization captures the column space in an orthonormal basis; solving least-squares problems then reduces
        to the triangular system <MathJax inline>{"\\( Rx=Q^\\top b \\)"}</MathJax>.
      </p>

      <h2 className="text-sm font-semibold mt-4 md:text-base">9. Solving Linear Systems Efficiently</h2>
      <ul className="list-disc pl-6 space-y-1">
        <li>
          <strong>LU/Cholesky:</strong> For <MathJax inline>{"\\( Ax=b \\)"}</MathJax>, factor once and solve via forward/back
          substitution. When many right-hand sides are required (e.g., parameter sweeps or time-stepping),
          this is asymptotically optimal.
        </li>
        <li>
          <strong>Least squares:</strong> For <MathJax inline>{"\\( m>n \\)"}</MathJax>, solve
          <MathJax inline>{"\\( \\min\\|Ax-b\\|_2 \\)"}</MathJax> by QR without squaring the condition number:
          compute <MathJax inline>{"\\( A=QR \\)"}</MathJax>, then <MathJax inline>{"\\( Rx=Q^\\top b \\)"}</MathJax>. Column-pivoted QR factorization (<MathJax inline>{"\\( AP=QR \\)"}</MathJax>) reveals numerical rank and enables truncated solutions.
        </li>
        <li>
          <strong>Rank deficiency:</strong> If columns are nearly dependent, QR with column pivoting identifies a stable
          subset; SVD refines this by quantifying singular directions and supports minimum-norm solutions.
        </li>
      </ul>

      <h2 className="text-sm font-semibold mt-4 md:text-base">10. QR Iteration for Eigenvalues</h2>
      <h3 className="font-semibold mt-2">Unshifted QR</h3>
      <MathJax>{String.raw`\[
      \begin{aligned}
        A_0&=A,\quad A_k=R_kQ_k,\\
         \ \text{where}\ A_{k-1}&=Q_kR_k (\text{QR factorization}).
        \end{aligned}
      \]`}</MathJax>
      <p>
        Each step is an orthogonal similarity,
        <MathJax inline>{"\\( A_k=Q_k^{\\top}A_{k-1}Q_k \\)"}</MathJax>, which preserves eigenvalues while tending toward
        triangular (or quasi-triangular) form. With a preliminary reduction to Hessenberg form, each iteration costs
        <MathJax inline>{"\\( \\mathcal{O}(n^2) \\)"}</MathJax>, making the method practical for dense eigenproblems.
      </p>

      <h3 className="font-semibold mt-2">Shifted QR (acceleration)</h3>
      <MathJax>{String.raw`\[
        A_{k-1}-\\mu_k I=Q_kR_k,\quad A_k=R_kQ_k+\\mu_k I.
      \]`}</MathJax>
      <p>
        Carefully chosen shifts <MathJax inline>{"\\( \\mu_k \\)"}</MathJax> (e.g., the <em>Wilkinson shift</em> from the trailing <MathJax inline>{"\\( 2\\times2 \\)"}</MathJax> block) dramatically accelerate convergence. For symmetric matrices,
        an initial reduction to tridiagonal form plus implicit shifts (bulge chasing) yields fast, reliable convergence
        with deflation as small subdiagonals vanish.
      </p>

      <h2 className="text-sm font-semibold mt-4 md:text-base">11. QR-Iteration Example (2×2)</h2>
      <p>
        Take <MathJax inline>{"\\( A=\\begin{bmatrix}2&1\\\\1&2\\end{bmatrix} \\)"}</MathJax> (eigenvalues 3 and 1).
      </p>
      <MathJax>{String.raw`\[
      \begin{aligned}
        A&=Q_1R_1,\\
        Q_1&=\begin{bmatrix}\tfrac{2}{\sqrt{5}}&-\tfrac{1}{\sqrt{5}}\\[2pt]\tfrac{1}{\sqrt{5}}&\tfrac{2}{\sqrt{5}}\end{bmatrix},\ \\ 
        R_1&=\begin{bmatrix}\sqrt{5}&\tfrac{4}{\sqrt{5}}\\[2pt]0&\tfrac{3}{\sqrt{5}}\end{bmatrix},
        \end{aligned}
      \]`}</MathJax>
      <MathJax>{String.raw`\[
        A_1=R_1Q_1=\begin{bmatrix}\tfrac{14}{5}&\tfrac{3}{5}\\[2pt]\tfrac{3}{5}&\tfrac{6}{5}\end{bmatrix}
        \approx \begin{bmatrix}2.8&0.6\\0.6&1.2\end{bmatrix}.
      \]`}</MathJax>
      <p>
        Repeating the process (with shifts) drives off-diagonal entries to zero, revealing the eigenvalues on the diagonal.
      </p>

      <h2 className="text-sm font-semibold mt-4 md:text-base">12. Numerical Stability & Conditioning</h2>
      <ul className="list-disc pl-6 space-y-1">
        <li>
          <strong>Pivoting in LU:</strong> Partial pivoting curbs element growth and is the default in dense solvers;
          equilibrating/scaling rows and columns can further reduce sensitivity.
        </li>
        <li>
          <strong>Cholesky:</strong> Backward stable for SPD problems; a breakdown (negative argument to a square root)
          indicates a violation of positive definiteness or significant roundoff.
        </li>
        <li>
          <strong>QR (Householder/Givens):</strong> Orthogonality preserves 2-norms and avoids squaring the condition
          number, making QR the recommended route for least-squares and as a building block in eigenvalue computations.
        </li>
        <li>
          <strong>Conditioning:</strong> Problem sensitivity is governed by
          <MathJax inline>{"\\( \\kappa_2(A)=\\|A\\|_2\\|A^{-1}\\|_2 \\)"}</MathJax>. Factorizations do not cure ill-conditioning,
          but stable ones (QR/Cholesky with proper assumptions) deliver trustworthy solutions up to the inherent
          sensitivity of the problem.
        </li>
      </ul>
    </div>
  );
}

